import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import toast, { Toaster } from 'react-hot-toast';
import { Crosshair2Icon, ExternalLinkIcon } from '@radix-ui/react-icons';
import {
  getSchoolLocationConfig,
  updateSchoolLocationConfig,
} from '../services/schoolLocationApi';
import useGeolocation, { geoDisponivel } from '../hooks/useGeolocation';
import './Global.css';
import './SchoolLocationConfigPage.css';

/**
 * Leitura do aparelho pior que isso não vira referência oficial (lição do
 * take-saúde: leitura imprecisa como referência cria "divergências" falsas
 * para sempre). O admin pode colar a coordenada do Google Maps no lugar.
 */
const MAX_PRECISAO_REFERENCIA = 150; // metros

// Mensagens próprias desta tela: aqui o admin captura a REFERÊNCIA, não bate ponto.
const mensagemCapturaFalha = (source) => {
  switch (source) {
    case 'denied':
      return 'Localização negada. Permita o acesso à localização para capturar a coordenada — ou cole a do Google Maps.';
    case 'unavailable':
      return 'O aparelho não conseguiu se localizar. Ative o GPS e tente de novo — ou cole a coordenada do Google Maps.';
    default:
      return 'A localização não respondeu a tempo. Tente em local aberto — ou cole a coordenada do Google Maps.';
  }
};

const raioValido = (v) => {
  const n = Number(v);
  return v !== '' && Number.isInteger(n) && n >= 20 && n <= 5000;
};

const formatDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '';
  }
};

/**
 * Tela admin da referência do colégio para o ponto pelo app:
 *  - "Usar minha localização atual" (admin em pé no colégio) ou coordenada
 *    manual do Google Maps;
 *  - raio permitido em metros;
 *  - link "Ver no mapa" para conferir antes de salvar.
 * A distância só AVISA (militar e admin); nunca bloqueia o registro.
 */
const SchoolLocationConfigPage = () => {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({ latitude: '', longitude: '', radiusMeters: 200 });
  const [leitura, setLeitura] = useState(null); // última captura: { accuracy, source }
  const [capturando, setCapturando] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const geo = useGeolocation();

  useEffect(() => {
    const load = async () => {
      try {
        const cfg = await getSchoolLocationConfig();
        setConfig(cfg);
        setForm({
          latitude: cfg.latitude ?? '',
          longitude: cfg.longitude ?? '',
          radiusMeters: cfg.radiusMeters ?? 200,
        });
      } catch (error) {
        toast.error('Erro ao carregar a configuração');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const usarMinhaLocalizacao = async () => {
    if (capturando) return;
    setCapturando(true);
    try {
      const pos = await geo.request();
      if (pos.source !== 'gps' && pos.source !== 'network') {
        toast.error(mensagemCapturaFalha(pos.source), { duration: 7000 });
        return;
      }
      if (pos.accuracy != null && pos.accuracy > MAX_PRECISAO_REFERENCIA) {
        toast.error(
          `Leitura imprecisa (±${Math.round(pos.accuracy)} m). Vá para um lugar aberto, ative a localização exata do aparelho e tente de novo — ou cole a coordenada do Google Maps.`,
          { duration: 9000 },
        );
        return;
      }
      setForm((f) => ({
        ...f,
        latitude: Number(pos.latitude.toFixed(6)),
        longitude: Number(pos.longitude.toFixed(6)),
      }));
      setLeitura({ accuracy: pos.accuracy, source: pos.source });
      const precisao = pos.accuracy != null ? ` (±${Math.round(pos.accuracy)} m)` : '';
      toast.success(`Localização capturada${precisao}. Confira no mapa e salve.`, {
        duration: 6000,
      });
    } finally {
      setCapturando(false);
    }
  };

  const handleSave = async () => {
    const lat = form.latitude === '' ? null : Number(form.latitude);
    const lng = form.longitude === '' ? null : Number(form.longitude);
    if ((lat == null) !== (lng == null)) {
      toast.error('Informe latitude e longitude juntas.');
      return;
    }
    if (!raioValido(form.radiusMeters)) {
      toast.error('O raio deve ser um número inteiro entre 20 e 5000 metros.');
      return;
    }
    setSaving(true);
    try {
      const updated = await updateSchoolLocationConfig({
        latitude: lat,
        longitude: lng,
        radiusMeters: Number(form.radiusMeters),
      });
      setConfig(updated);
      toast.success(
        updated.latitude != null
          ? 'Localização do colégio salva!'
          : 'Referência removida — avisos de distância desligados.',
      );
    } catch (error) {
      const m = error.response?.data?.message;
      toast.error('Erro ao salvar: ' + (Array.isArray(m) ? m.join(' ') : m || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner" />
          </div>
        </div>
      </>
    );
  }

  const temCoordenada = form.latitude !== '' && form.longitude !== '';
  const configurado = config?.latitude != null && config?.longitude != null;

  return (
    <>
      <Toaster position="top-right" />
      <Navbar />
      <div className="container">
        <div className="page-header-new">
          <div>
            <h1>Localização do Colégio</h1>
            <p className="subtitle">
              Ponto de referência e raio para o ponto batido pelo militar no app
            </p>
          </div>
        </div>

        <div className="info-card info-card-blue">
          <div className="info-card-body">
            <p>
              <strong>Como funciona:</strong> quando um militar do efetivo registra entrada ou
              saída pelo próprio celular, o sistema mede a distância entre o aparelho e este
              ponto. Dentro do raio, nada muda. Fora dele, o militar é avisado antes de
              registrar e o registro fica marcado como <strong>fora do raio</strong> na lista
              de registros e no painel do fiscal — o ponto <strong>nunca é bloqueado</strong>{' '}
              por distância.
            </p>
            <p>Sem coordenada cadastrada, a distância não é calculada e nenhum aviso aparece.</p>
          </div>
        </div>

        <div className="info-card locpg-card">
          <div className="info-card-body">
            <div className="locpg-status">
              {configurado ? (
                <span className="locpg-pill ok">● Referência cadastrada</span>
              ) : (
                <span className="locpg-pill off">○ Sem referência — avisos desligados</span>
              )}
            </div>

            <div className="locpg-capture">
              <button
                type="button"
                className="btn-secondary locpg-capture-btn"
                onClick={usarMinhaLocalizacao}
                disabled={capturando || !geoDisponivel()}
              >
                <Crosshair2Icon width={16} height={16} />
                {capturando ? 'Obtendo localização…' : 'Usar minha localização atual'}
              </button>
              <small className="form-hint">
                Fique no colégio (de preferência a céu aberto) e toque no botão: a coordenada
                do seu aparelho preenche os campos. Leituras piores que ±{MAX_PRECISAO_REFERENCIA} m
                não são aceitas. Você também pode colar a coordenada do Google Maps.
              </small>
              {!geoDisponivel() && (
                <small className="form-hint locpg-warn">
                  Este navegador não fornece localização — preencha manualmente.
                </small>
              )}
              {geo.permission === 'denied' && (
                <small className="form-hint locpg-warn">
                  Localização bloqueada no navegador (cadeado ao lado do endereço → Localização →
                  Permitir).
                </small>
              )}
              {leitura && (
                <small className="form-hint">
                  Última leitura:{' '}
                  {leitura.accuracy != null ? `±${Math.round(leitura.accuracy)} m` : 'sem precisão informada'}{' '}
                  ({leitura.source === 'gps' ? 'GPS' : 'rede'})
                </small>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="locpg-lat">Latitude</label>
                <input
                  id="locpg-lat"
                  type="number"
                  step="0.000001"
                  min="-90"
                  max="90"
                  placeholder="-3.732700"
                  value={form.latitude}
                  onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="locpg-lng">Longitude</label>
                <input
                  id="locpg-lng"
                  type="number"
                  step="0.000001"
                  min="-180"
                  max="180"
                  placeholder="-38.527000"
                  value={form.longitude}
                  onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="locpg-raio">Raio permitido (metros)</label>
                <input
                  id="locpg-raio"
                  type="number"
                  min="20"
                  max="5000"
                  step="10"
                  value={form.radiusMeters}
                  onChange={(e) => setForm({ ...form, radiusMeters: e.target.value })}
                />
                <small className={`form-hint${raioValido(form.radiusMeters) ? '' : ' locpg-warn'}`}>
                  {raioValido(form.radiusMeters)
                    ? '200 m é o padrão. O pátio é grande e o GPS dentro do prédio erra dezenas de metros — dê folga.'
                    : 'Informe um número inteiro entre 20 e 5000 metros.'}
                </small>
              </div>
            </div>

            {temCoordenada && (
              <a
                className="locpg-map"
                href={`https://www.google.com/maps?q=${form.latitude},${form.longitude}`}
                target="_blank"
                rel="noreferrer"
              >
                <ExternalLinkIcon width={14} height={14} /> Ver no mapa
              </a>
            )}

            <div className="locpg-actions">
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar configuração'}
              </button>
              <span className="locpg-updated">
                {config?.updatedByName
                  ? `Atualizado por ${config.updatedByName} em ${formatDateTime(config.updatedAt)}`
                  : 'Nunca configurado'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SchoolLocationConfigPage;
